<template>
  <Teleport v-if="isOpen" to="#teleports">
    <div
      ref="modal"
      :role="role"
      aria-modal="true"
      :aria-labelledby="ariaLabelledby"
      :aria-describedby="ariaDescribedby"
      :class="$style.modal"
      tabindex="-1"
      @keydown="onKeydown"
      @keyup="onKeyup"
    >
      <div
        :class="$style.overlay"
        data-base-modal-overlay
        :style="{ backgroundColor: overlayColor }"
        @click="onOverlayClick"
      />
      <slot />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import {
  acquireBodyScrollLock,
  releaseBodyScrollLock,
} from '~/utils/bodyScrollLock'

type DialogRole = 'dialog' | 'alertdialog'

interface ModalEnvironment {
  activeCount: number
}

type BaseModalGlobal = typeof globalThis & {
  __wpnBaseModalEnvironment?: ModalEnvironment
}

const getModalEnvironment = (): ModalEnvironment => {
  const globalScope = globalThis as BaseModalGlobal
  if (!globalScope.__wpnBaseModalEnvironment) {
    globalScope.__wpnBaseModalEnvironment = {
      activeCount: 0,
    }
  }
  return globalScope.__wpnBaseModalEnvironment
}

const focusableSelector = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const acquireModalEnvironment = () => {
  const environment = getModalEnvironment()
  if (environment.activeCount === 0) acquireBodyScrollLock()
  environment.activeCount += 1
}

const releaseModalEnvironment = () => {
  const environment = getModalEnvironment()
  if (environment.activeCount === 0) return

  environment.activeCount -= 1
  if (environment.activeCount > 0) return

  releaseBodyScrollLock()
}

const emit = defineEmits(['modalClosed', 'modalOpened', 'requestClose'])

const props = withDefaults(
  defineProps<{
    ariaDescribedby?: string
    ariaLabelledby?: string
    initialFocusSelector?: string
    overlayColor?: string
    role?: DialogRole
    shouldCloseOnEscape?: boolean
    shouldCloseOnOverlayClick?: boolean
    isOpen: boolean
  }>(),
  {
    ariaDescribedby: undefined,
    ariaLabelledby: undefined,
    initialFocusSelector: undefined,
    overlayColor: undefined,
    role: 'dialog',
    shouldCloseOnEscape: true,
    shouldCloseOnOverlayClick: false,
  },
)

const modal = ref<HTMLElement | null>(null)
const openedAt = ref<number | null>(null)
const previousActiveElement = ref<HTMLElement | null>(null)
const environmentLocked = ref(false)
const escapeKeydownSeen = ref(false)

const getFocusableElements = () => {
  if (!modal.value) return []
  return Array.from(
    modal.value.querySelectorAll<HTMLElement>(focusableSelector),
  ).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      !element.closest('[hidden], [aria-hidden="true"], [inert]'),
  )
}

const focusInitialElement = () => {
  if (!modal.value) return

  const focusable = getFocusableElements()
  let target: HTMLElement | null = null
  if (props.initialFocusSelector) {
    try {
      target = modal.value.querySelector<HTMLElement>(
        props.initialFocusSelector,
      )
    } catch {
      target = null
    }
  }
  if (
    target?.matches(':disabled') ||
    target?.closest('[hidden], [aria-hidden="true"], [inert]')
  ) {
    target = null
  }
  target ||=
    focusable.find((element) => element.hasAttribute('autofocus')) || null
  target ||= focusable[0] || modal.value
  target.focus()
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    if (!props.shouldCloseOnEscape) return
    event.preventDefault()
    escapeKeydownSeen.value = true
    emit('requestClose')
    return
  }

  if (event.key !== 'Tab' || !modal.value) return

  const focusable = getFocusableElements()
  if (!focusable.length) {
    event.preventDefault()
    modal.value.focus()
    return
  }

  const activeElement = document.activeElement as HTMLElement | null
  const activeIndex = focusable.indexOf(activeElement as HTMLElement)
  if (event.shiftKey && (activeIndex === 0 || activeIndex === -1)) {
    event.preventDefault()
    focusable[focusable.length - 1].focus()
  } else if (
    !event.shiftKey &&
    (activeIndex === focusable.length - 1 || activeIndex === -1)
  ) {
    event.preventDefault()
    focusable[0].focus()
  }
}

// Keep compatibility with consumers that dispatch/listen for the previous
// keyup-based Escape interaction while avoiding a duplicate request for a
// normal keydown/keyup pair.
const onKeyup = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  if (escapeKeydownSeen.value) {
    escapeKeydownSeen.value = false
    return
  }
  if (props.shouldCloseOnEscape) emit('requestClose')
}

const onOverlayClick = () => {
  if (props.shouldCloseOnOverlayClick) emit('requestClose')
}

const restoreFocus = () => {
  if (previousActiveElement.value?.isConnected) {
    previousActiveElement.value.focus()
  }
  previousActiveElement.value = null
}

const openModal = async () => {
  if (environmentLocked.value || !import.meta.client) return

  environmentLocked.value = true
  previousActiveElement.value = document.activeElement as HTMLElement
  openedAt.value = Date.now()
  acquireModalEnvironment()
  emit('modalOpened')

  await nextTick()
  if (props.isOpen) focusInitialElement()
}

const closeModal = () => {
  if (!environmentLocked.value) return

  const duration = (Date.now() - (openedAt.value || Date.now())) / 1000
  emit('modalClosed', Math.round(duration))
  releaseModalEnvironment()
  environmentLocked.value = false
  escapeKeydownSeen.value = false
  openedAt.value = null
  restoreFocus()
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) openModal()
    else closeModal()
  },
)

onMounted(() => {
  if (props.isOpen) openModal()
})

onBeforeUnmount(() => {
  closeModal()
})
</script>

<style lang="scss" module>
.modal {
  color: var(--black);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  z-index: var(--modal-z-index);
}

.overlay {
  position: absolute;
  inset: 0;
  z-index: -1;
}

// Cookie consent must remain actionable even when an application modal is
// open, so keep it above the modal's stacking context.
:global(#ketch-consent-banner) {
  position: relative;
  z-index: calc(var(--modal-z-index, 1000) + 1);
}
</style>
